import React, { useState, useEffect, useMemo } from 'react';
import AssetModalWrapper from './AssetModal.styles';
import IntlMessages from '@iso/components/utility/intlMessages';
import { Button, Form, Select, Row, Col, Input, Table, Tag, Tooltip, Space, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
    formService,
    assetFormService,
    categoryGlobalFormService,
    subCategoryGlobalFormService,
    globalFormService,
} from '@iso/services';
import CreateFormModal from '@iso/containers/Pages/Form/FormModal';

const { Option } = Select;

// The asset "Add Form" picker defaults to this category so the common case
// (attaching a Pre-Start to a vehicle/plant asset) needs no extra clicks.
const DEFAULT_CATEGORY_NAME = 'Pre-Starts';

const FormModal = (props) => {
    const [forms, setForms] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [categoryId, setCategoryId] = useState(undefined);
    const [subCategoryId, setSubCategoryId] = useState(undefined);
    const [search, setSearch] = useState('');
    // id of the form currently being attached (shows a spinner on its Select button)
    const [savingId, setSavingId] = useState(null);
    // the form being edited via the row "Edit" button (opens the full editor)
    const [editingForm, setEditingForm] = useState(null);
    const [visibleEditModal, setVisibleEditModal] = useState(false);
    // globalFormId -> sub_category_global_form_id. Per-property `forms` don't
    // store a sub-category of their own; they inherit it from the library
    // template they were adopted from (source_global_form_id / global_form_id),
    // so we map through this to filter by sub-category.
    const [subCatByGlobalForm, setSubCatByGlobalForm] = useState({});

    const {
        propertyId,
        onCancelAddForm,
        visibleAddForm,
        assetId,
        onAdded,
        record = null,
        mode = 'add',
        onEdited = () => {},
    } = props;

    // --- data loads (only while the modal is open) ---
    useEffect(() => {
        if (!propertyId || !visibleAddForm) return;
        fetchForms();
        fetchCategories();
        fetchGlobalFormSubCategoryMap();
    }, [propertyId, visibleAddForm]);

    const fetchForms = () => {
        formService.getList(propertyId).then(res => {
            if (res && res.code === '0000') {
                setForms(res.forms || []);
            } else {
                message.error((res && res.message) || 'Failed to fetch forms');
            }
        });
    };

    const fetchCategories = () => {
        categoryGlobalFormService.get().then(res => {
            if (res && res.code === '0000') {
                setCategories(res.category_global_form || []);
            }
        });
    };

    const fetchGlobalFormSubCategoryMap = () => {
        globalFormService.list({ limit: 1000 }).then(res => {
            const map = {};
            (res && Array.isArray(res.global_forms) ? res.global_forms : []).forEach(gf => {
                if (gf && gf.id != null) {
                    map[gf.id] = gf.sub_category_global_form_id ?? null;
                }
            });
            setSubCatByGlobalForm(map);
        });
    };

    // Default the category filter: in edit mode follow the linked form's own
    // category (so it's visible), otherwise default to Pre-Starts once the
    // category list has loaded.
    useEffect(() => {
        if (!visibleAddForm || categories.length === 0) return;
        if (mode === 'edit' && record?.form?.category_global_form_id) {
            setCategoryId(record.form.category_global_form_id);
            return;
        }
        const preStarts = categories.find(c => c.name === DEFAULT_CATEGORY_NAME);
        setCategoryId(preStarts ? preStarts.id : undefined);
    }, [visibleAddForm, categories, mode, record]);

    // Cascade: (re)load sub-categories whenever the selected category changes.
    useEffect(() => {
        if (!categoryId) {
            setSubCategories([]);
            return;
        }
        subCategoryGlobalFormService.get(categoryId).then(res => {
            setSubCategories(res && res.code === '0000' ? (res.sub_category_global_forms || []) : []);
        });
    }, [categoryId]);

    // Reset the volatile picker state each time the modal (re)opens.
    useEffect(() => {
        if (!visibleAddForm) return;
        setSearch('');
        setSubCategoryId(undefined);
        setSavingId(null);
    }, [visibleAddForm]);

    const subCategoryOfForm = (f) => {
        const globalId = f.source_global_form_id || f.global_form_id;
        return globalId ? subCatByGlobalForm[globalId] : null;
    };

    const filteredForms = useMemo(() => {
        const term = search.trim().toLowerCase();
        return forms.filter(f => {
            if (categoryId && f.category_global_form_id !== categoryId) return false;
            if (subCategoryId && subCategoryOfForm(f) !== subCategoryId) return false;
            if (term && !(f.name || '').toLowerCase().includes(term)) return false;
            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forms, categoryId, subCategoryId, search, subCatByGlobalForm]);

    // Keep the currently-linked form in the table in edit mode even if the
    // active filters would otherwise hide it.
    const tableData = useMemo(() => {
        if (mode === 'edit' && record?.form && !filteredForms.some(f => f.id === record.form.id)) {
            return [record.form, ...filteredForms];
        }
        return filteredForms;
    }, [filteredForms, mode, record]);

    const handleCategoryChange = (value) => {
        setCategoryId(value);
        setSubCategoryId(undefined);
    };

    // Open the full form editor for a row (edit name / schema / publish etc.).
    const openEditForm = (row) => {
        setEditingForm(row);
        setVisibleEditModal(true);
    };
    const closeEditForm = () => {
        setVisibleEditModal(false);
        setEditingForm(null);
    };

    // One click on a row's "Select" attaches that form to the asset and closes.
    const handleSelect = (formId) => {
        if (savingId != null) return;
        setSavingId(formId);
        const data = { form_id: formId, asset_id: assetId };
        const id = record?.id || null;

        assetFormService.storeOrUpdate(data, id).then(res => {
            if (res.code === '0000') {
                if (mode === 'edit') {
                    onEdited(id, res.assetForm);
                } else {
                    onAdded(res.assetForm);
                }
                onCancelAddForm();
                return;
            }
            if (res.code === '0001' && Array.isArray(res.assetForm)) {
                message.error(res.assetForm.map(e => e.message).join(', '));
            }
        })
        .catch(error => {
            console.error('Error storing form:', error);
            message.error('Failed to store form');
        })
        .finally(() => {
            setSavingId(null);
        });
    };

    const filterByChildren = (input, option) =>
        (option?.children || '').toLowerCase().includes(input.toLowerCase());

    // Only a Published (public) form has a submission link/token, so only those
    // can be attached to an asset — a Private form must be published first.
    const isFormPublic = (f) => f?.is_public === 1 || f?.is_public === true;

    const columns = [
        {
            title: 'Form name',
            dataIndex: 'name',
            key: 'name',
            render: (_, r) => (
                <div>
                    <div><strong>{r.name || '-'}</strong></div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                        ID: {r.id ?? '—'}
                    </div>
                </div>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category_global_form_data',
            key: 'category',
            width: 160,
            render: (cat) => cat?.name || '—',
        },
        {
            title: 'Status',
            dataIndex: 'is_public',
            key: 'status',
            width: 110,
            render: (_, r) => (
                isFormPublic(r)
                    ? <Tag color="green">Public</Tag>
                    : <Tag>Private</Tag>
            ),
        },
        {
            title: '',
            key: 'action',
            width: 170,
            align: 'right',
            render: (_, r) => (
                <Space size={8}>
                    <Button size="small" onClick={() => openEditForm(r)}>Edit</Button>
                    {isFormPublic(r) ? (
                        <Button
                            size="small"
                            type="primary"
                            ghost
                            loading={savingId === r.id}
                            disabled={savingId != null && savingId !== r.id}
                            onClick={() => handleSelect(r.id)}
                        >
                            Select
                        </Button>
                    ) : (
                        <Tooltip title="You need Publish Form to use it">
                            {/* span wrapper so the tooltip still shows on a disabled button */}
                            <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                                <Button size="small" disabled style={{ pointerEvents: 'none' }}>Select</Button>
                            </span>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <AssetModalWrapper
            visible={visibleAddForm}
            onCancel={onCancelAddForm}
            width={820}
            title={ mode === 'edit' ? <IntlMessages id="propertyPage.mobModal.formTab.edit" /> : <IntlMessages id="propertyPage.mobModal.formTab.chooseButton" />}
            footer={null}
        >
            <Form
                layout="vertical"
                id="add-form-modal"
                className="form-modal-content"
            >
                <Row gutter={12} className="form-filter" style={{ marginBottom: 8 }}>
                    <Col xs={{ span: 24 }} md={{ span: 8 }}>
                        <Form.Item label="Category">
                            <Select
                                allowClear
                                showSearch
                                placeholder="All categories"
                                optionFilterProp="children"
                                value={categoryId}
                                onChange={handleCategoryChange}
                                filterOption={filterByChildren}
                            >
                                {categories.map((c) => (
                                    <Option key={c.id} value={c.id}>{c.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={{ span: 24 }} md={{ span: 8 }}>
                        <Form.Item label="Sub-category">
                            <Select
                                allowClear
                                showSearch
                                placeholder={categoryId ? 'All sub-categories' : 'Select a category first'}
                                optionFilterProp="children"
                                value={subCategoryId}
                                onChange={setSubCategoryId}
                                disabled={!categoryId}
                                filterOption={filterByChildren}
                            >
                                {subCategories.map((s) => (
                                    <Option key={s.id} value={s.id}>{s.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={{ span: 24 }} md={{ span: 8 }}>
                        <Form.Item label="Search">
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                placeholder="Search form name"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item label="Select a form" style={{ marginBottom: 0 }}>
                    <Table
                        rowKey="id"
                        size="small"
                        columns={columns}
                        dataSource={tableData}
                        pagination={{ pageSize: 5, size: 'small', showSizeChanger: false }}
                        scroll={{ y: 260 }}
                        locale={{ emptyText: 'No forms match the selected filters' }}
                    />
                </Form.Item>
            </Form>

            {/* Full form editor opened from a row's "Edit" button. On save it
                refreshes the picker list so name/status changes show immediately. */}
            <CreateFormModal
                visibleFormModal={visibleEditModal}
                cancelModal={closeEditForm}
                propertyId={propertyId}
                formData={editingForm || {}}
                updateFormData={fetchForms}
            />
        </AssetModalWrapper>
    );
};

export default FormModal;
